import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import JsonLd, { articleSchema } from '../components/JsonLd';

const Example = ({ children }) => (
  <div className="sf-example">
    <div className="sf-example-label">Real-world example</div>
    <p>{children}</p>
  </div>
);

const InvestmentBasics = () => {
  return (
    <>
      <SEO
        title="Investment Basics - Types of Investments & Portfolio Building | StckFuture"
        description="Learn investment basics including stocks, bonds, ETFs, dollar-cost averaging, compound interest, and how to build a diversified investment portfolio."
        canonical="https://www.stckfuture.com/investment-basics"
      />
      <JsonLd schema={articleSchema({
        title: 'Investment Basics - Types of Investments & Portfolio Building | StckFuture',
        description: 'Learn investment basics including stocks, bonds, ETFs, dollar-cost averaging, compound interest, and how to build a diversified investment portfolio.',
        url: 'https://www.stckfuture.com/investment-basics',
        dateModified: '2026-06-13',
      })} />
      <div className="sf-content-page">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">

              <div className="sf-page-header">
                <span className="sf-eyebrow">Module 2</span>
                <h1>Investment Basics</h1>
                <p className="lead">Understand the different types of investments, how to build a portfolio, and why starting early matters more than starting with a lot.</p>
              </div>

              {/* Types of Investments */}
              <div className="sf-card">
                <h3>Types of investments</h3>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Stocks</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Ownership shares in companies. Highest potential returns but also highest volatility. Ideal for long-term growth and can generate income through dividends.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Bonds</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Loans to companies or governments that pay regular interest. Lower returns than stocks but less volatility. Provides steady income and balances portfolio risk.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>ETFs</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Exchange-traded funds — baskets of securities that trade like individual stocks. Instant diversification, typically lower fees than mutual funds, trade throughout the day.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Real estate</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Property investments that generate rental income and appreciate in value. Can be owned directly or through REITs. Offers tax advantages and inflation hedging.</p>
                  </div>
                </div>
                <Example>Warren Buffett's Berkshire Hathaway purchased $1.3 billion of Coca-Cola shares in 1988. Today that investment is worth over $25 billion and generates ~$640 million in annual dividends — showing how quality stocks can both appreciate and provide growing income over decades.</Example>
              </div>

              {/* Building a Portfolio */}
              <div className="sf-card">
                <h3>Building a portfolio</h3>
                <p>Asset allocation — how you divide investments among asset classes — is the most important portfolio decision you'll make. It depends on three things:</p>
                <ul className="mb-3">
                  <li className="mb-1"><strong>Time horizon</strong> — how long until you need the money</li>
                  <li className="mb-1"><strong>Risk tolerance</strong> — your comfort level with market swings</li>
                  <li className="mb-1"><strong>Financial goals</strong> — retirement, home purchase, etc.</li>
                </ul>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--green)', marginBottom: 8 }}>CONSERVATIVE</div>
                      <p className="sf-text-small mb-1"><strong>Stocks:</strong> 20–30%</p>
                      <p className="sf-text-small mb-1"><strong>Bonds:</strong> 50–60%</p>
                      <p className="sf-text-small mb-3"><strong>Cash:</strong> 10–20%</p>
                      <p className="sf-text-small sf-text-muted mb-0">Best for short time horizons or low risk tolerance</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--green)', marginBottom: 8 }}>MODERATE</div>
                      <p className="sf-text-small mb-1"><strong>Stocks:</strong> 50–60%</p>
                      <p className="sf-text-small mb-1"><strong>Bonds:</strong> 30–40%</p>
                      <p className="sf-text-small mb-3"><strong>Cash:</strong> 5–10%</p>
                      <p className="sf-text-small sf-text-muted mb-0">Best for medium time horizons or moderate risk tolerance</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--green)', marginBottom: 8 }}>AGGRESSIVE</div>
                      <p className="sf-text-small mb-1"><strong>Stocks:</strong> 70–80%</p>
                      <p className="sf-text-small mb-1"><strong>Bonds:</strong> 15–25%</p>
                      <p className="sf-text-small mb-3"><strong>Cash:</strong> 0–5%</p>
                      <p className="sf-text-small sf-text-muted mb-0">Best for long time horizons or high risk tolerance</p>
                    </div>
                  </div>
                </div>
                <Example>During the 2008 financial crisis, a diversified 60/40 (stocks/bonds) portfolio lost ~20% — painful, but far better than the S&P 500's 37% decline. It also recovered within 3 years. Diversification won't eliminate losses but it reliably reduces them.</Example>
              </div>

              {/* Investment Accounts */}
              <div className="sf-card">
                <h3>Investment accounts</h3>
                <p>Where you hold investments matters as much as what you hold, because of tax implications.</p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Tax-advantaged accounts</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>401(k)/403(b)</strong> — employer-sponsored, tax-deferred growth</li>
                      <li className="mb-1"><strong>Traditional IRA</strong> — tax-deductible contributions, deferred growth</li>
                      <li className="mb-1"><strong>Roth IRA</strong> — after-tax contributions, tax-free withdrawals</li>
                      <li className="mb-1"><strong>HSA</strong> — triple tax advantage for medical expenses</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <strong>Taxable accounts</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>Brokerage accounts</strong> — flexible, no contribution limits</li>
                      <li className="mb-1"><strong>Robo-advisors</strong> — automated, low-fee portfolios</li>
                      <li className="mb-1"><strong>Direct stock purchase</strong> — buy shares directly from companies</li>
                    </ul>
                  </div>
                </div>
                <Example>Two investors each put in $6,000/year for 30 years at 8% returns. Investor A uses a Roth IRA; Investor B uses a taxable account paying 22% tax on gains. After 30 years: Investor A has ~$680,000; Investor B has ~$500,000. The $180,000 gap comes purely from tax treatment.</Example>
              </div>

              {/* Dollar-Cost Averaging */}
              <div className="sf-card">
                <h3>Dollar-cost averaging</h3>
                <p>Dollar-cost averaging (DCA) means investing a fixed amount at regular intervals regardless of market conditions. It removes the impossible task of timing the market and enforces discipline automatically.</p>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Reduces timing risk</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Avoids the risk of investing a large lump sum at a market peak.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Buys more when prices are low</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Your fixed amount buys more shares when prices drop — automatic bargain hunting.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Removes emotion</strong>
                    <p className="sf-text-small sf-text-muted mt-1">A systematic schedule means you don't have to decide each month whether to invest.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Accessible</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Works with small amounts — you don't need a large lump sum to start.</p>
                  </div>
                </div>
                <Example>During the 2008–2009 crisis, an investor who kept investing $500/month in an S&P 500 index fund bought more shares at every price drop. By 2012, their portfolio had fully recovered and was in profit — despite investing through one of the worst crashes in history.</Example>
              </div>

              {/* Compound Interest */}
              <div className="sf-card">
                <h3>Compound interest</h3>
                <p>Compound interest means you earn returns not just on your initial investment, but on all the accumulated returns too. Over long periods, this creates exponential growth.</p>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>The Rule of 72</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Divide 72 by your expected annual return to estimate how long it takes your money to double. At 8% returns: 72 ÷ 8 = 9 years to double.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Time is the key variable</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Starting early — even with smaller amounts — consistently outperforms starting later with larger amounts.</p>
                  </div>
                </div>
                <Example>$10,000 invested at age 25 at 8% annual return = ~$217,000 by age 65, with no additional contributions. Wait until age 35? You'd have only ~$100,000 by 65. That $117,000 difference comes entirely from starting 10 years earlier — not from investing more.</Example>
              </div>

              {/* Investment Fees */}
              <div className="sf-card">
                <h3>Investment fees</h3>
                <p>Small percentage fees compound over time and can dramatically reduce your final balance. Minimising fees is one of the highest-impact decisions you can make.</p>
                <ul className="mb-3">
                  <li className="mb-2"><strong>Expense ratios</strong> — annual fees charged by funds. Range from 0.03% for low-cost index funds to 1%+ for actively managed funds.</li>
                  <li className="mb-2"><strong>Trading commissions</strong> — many brokers now offer commission-free trading for stocks and ETFs.</li>
                  <li className="mb-2"><strong>Advisory fees</strong> — typically 0.5–1.5% of assets annually for a managed account.</li>
                  <li className="mb-2"><strong>Load fees</strong> — sales charges on some mutual funds. Front-end (paid when buying) or back-end (paid when selling). Avoidable with most modern funds.</li>
                </ul>
                <Example>Two investors put $100,000 in for 30 years at 8% before fees. Investor A picks a fund with 0.1% expense ratio; Investor B picks one with 1.0%. After 30 years: Investor A has ~$960,000; Investor B has ~$760,000. A seemingly small 0.9% fee difference costs $200,000 over time.</Example>
              </div>

              {/* Rebalancing */}
              <div className="sf-card">
                <h3>Rebalancing your portfolio</h3>
                <p>Rebalancing means periodically realigning your asset mix back to your target allocation. Without it, a long bull market in stocks can leave you much more exposed to equity risk than you intended.</p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Time-based rebalancing</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Rebalance on a fixed schedule (quarterly, semi-annually, annually). Simple to implement and removes emotion from the process.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Threshold-based rebalancing</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Rebalance when allocations drift beyond a set threshold (e.g. ±5%). More responsive to markets, but requires closer monitoring.</p>
                  </div>
                </div>
                <Example>An investor starts 2019 with a 60/40 portfolio. After a strong year for stocks, it drifted to 68/32. Rebalancing back to 60/40 meant selling some stocks and buying bonds. When markets crashed in March 2020, the rebalanced portfolio was less exposed to the drop — and had more bonds to sell low and buy equities cheap.</Example>
              </div>

              {/* Key Takeaways */}
              <div className="sf-takeaways">
                <h3>Key takeaways</h3>
                <ul>
                  <li>Stocks, bonds, ETFs, and real estate each serve different roles in a portfolio</li>
                  <li>Asset allocation should match your time horizon, risk tolerance, and goals</li>
                  <li>Tax-advantaged accounts (Roth IRA, 401k) can add tens of thousands to your final balance</li>
                  <li>Dollar-cost averaging removes timing risk and enforces discipline automatically</li>
                  <li>Compound interest rewards starting early above all else — time is more valuable than amount</li>
                  <li>Small differences in fees compound into large differences in outcomes over 20–30 years</li>
                  <li>Rebalance regularly to maintain your intended risk level</li>
                </ul>
              </div>

              <div className="sf-next-page">
                <Link to="/trading-principles" className="btn-sf-primary">
                  Next: Trading Principles →
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InvestmentBasics;
